import { LoaderGlobal } from '@bloomays-lib/ui.shared';
import { Suspense, useEffect } from 'react';
import { useAuthStore, useUserStore } from '../stores';
import { useApolloClient } from '@apollo/client';
import { Logger } from '../services/serviceLogger';
import { isAuth0SessionExpiredError } from '@bloomays-lib/common.auth/browser';

const logger = Logger('UserProvider');

const UserData = ({ children }: { children: React.ReactNode }): React.ReactNode => {
  const user = useUserStore((state) => state.user);
  const userError = useUserStore((state) => state.userError);
  const auth = useAuthStore((state) => state.auth);
  const userFetch = useUserStore((state) => state.userFetch);
  const userInit = useUserStore((state) => state.userInit);
  const setApolloClient = useUserStore((state) => state.setApolloClient);
  const clearUserError = useUserStore((state) => state.clearUserError);
  const apolloClient = useApolloClient();

  useEffect(() => {
    (async () => {
      if (auth?.isAuthenticated === false) {
        await userInit();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Handle Auth0 session expired errors from GraphQL queries (userStore)
  if (userError) {
    if (isAuth0SessionExpiredError(userError) && auth?.logout) {
      logger.debug('Auth0 session expired during user fetch, redirecting to login');
      // Clear the error state and redirect to login
      clearUserError();
      auth.logout();
      return (<LoaderGlobal waitingText="Session expirée, redirection vers la connexion..." />) as React.ReactNode;
    }

    // For other user errors, throw as before
    throw userError;
  }

  if (auth?.isAuthenticated && user === undefined) {
    if (apolloClient) {
      setApolloClient(apolloClient);
    }
    throw userFetch('!user');
  }

  return children as React.ReactNode;
};

export function UserProvider({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoaderGlobal waitingText="Chargement du profil utilisateur"></LoaderGlobal>}>
      <UserData>{children}</UserData>
    </Suspense>
  ) as React.ReactNode;
}
